import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import endConsumerV3Api from '../../../../utils/api/v2/end-consumer';

interface AddWalletProperties {
  currency: string;
  amount: number | null;
  reason: string;
}

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * Component to add or deduct money to/from a user's wallet
 * @param {object} param0 - The properties object
 * @param {() => void} param0.closeModal - The closeModal prop
 * @param {string} param0.endConsumerId - The endConsumerId prop
 * @param {'add' | 'deduct'} [param0.actionType] - 'add' or 'deduct'
 * @param {() => void} [param0.onSuccess] - The onSuccess callback function
 * @returns {JSX.Element} - The AddWallet component
 */
function AddWallet({
  closeModal,
  endConsumerId,
  actionType = 'add',
  onSuccess,
}: {
  closeModal: () => void;
  endConsumerId: string;
  actionType?: 'add' | 'deduct';
  onSuccess?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('endConsumer');
  const branding = useAppBranding();
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [wallet, setWallet] = useState<AddWalletProperties>({ currency: 'SAR', amount: null, reason: '' });
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  /**
   * Handles input changes for the wallet amount and currency
   * @param {string} name - The input name
   * @param {string | number} value - The input value
   */
  const handleInputChange = (name: string, value: string | number) => {
    setWallet((previous) => ({ ...previous, [name]: value }));
  };

  /**
   * Handles the submission of the add/deduct wallet form
   */
  const handleSubmit = async () => {
    const validatedEndConsumerId = endConsumerId?.trim();
    if (!validatedEndConsumerId) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            actionType === 'add'
              ? t('unableToAddWalletFundsWithoutAValidEndConsumerID')
              : t('unableToDeductWalletFundsWithoutAValidEndConsumerID'),
          type: 'error',
          code: 0,
        }),
      );
      return;
    }
    setLoading(true);
    try {
      const payload: any = {
        amount: wallet.amount,
        currency: wallet.currency,
        ...(wallet.reason && { reason: wallet.reason }),
      };

      const response =
        actionType === 'add'
          ? await endConsumerV3Api.topUpWallet(validatedEndConsumerId, payload)
          : await endConsumerV3Api.deductWallet(validatedEndConsumerId, payload);

      if (response?.status >= 200 && response?.status < 300) {
        setOpenStatus(true);
        setModalView({
          title: '',
          content: (
            <SuccessNotification
              title={actionType === 'add' ? t('amountAddedSuccessfully') : t('amountDeductedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setOpenStatus(false);
                closeModal();
                if (onSuccess) {
                  onSuccess();
                }
              }}
            />
          ),
          header: false,
          rightCorner: '',
          notification: true,
        });
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('somethingWentWrong'),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-money" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        closeModal={() => setOpenStatus(false)}
        notification={modalView.notification}
      />
      <div className="add-money__inputs">
        <CustomInput
          name="amount"
          type="number"
          value={wallet.amount}
          placeholder={t('enterAnAmount')}
          onChangeValue={handleInputChange}
          maxValue={10000}
          decimalLimit={2}
        />
        <SelectField
          name="currency"
          onChangeValue={(name, value) =>
            setWallet((previous) => ({ ...previous, currency: value }))
          }
          options={branding.text.currency.split(',').map((option) => ({
            label: option.trim(),
            value: option.trim(),
          }))}
          selectedValue={wallet.currency}
        />
      </div>
      <div className="add-money__inputs">
        <CustomInput
          name="reason"
          type="text"
          value={wallet.reason}
          placeholder={t('enterReason')}
          onChangeValue={handleInputChange}
        />
      </div>
      <div className="add-money__action">
        <MyButton
          label={t('cancel')}
          buttonType="error"
          onClickFunc={closeModal}
          disableButton={loading}
        />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={wallet.amount !== null ? handleSubmit : undefined}
          disableButton={loading || wallet.amount === null}
          loading={loading}
        />
      </div>
    </div>
  );
}

AddWallet.defaultProps = {
  actionType: 'add',
  onSuccess: undefined,
};

export default AddWallet;
